import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { Checkbox } from '../components/ui/checkbox';
import {
  Plus, Search, Filter, MoreHorizontal, Mail, Phone,
  User, Edit, Trash2, Scissors, CalendarDays, Cake, Crown
} from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';

const statusColors = {
  lead: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20',
  customer: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20',
  inactive: 'bg-gray-500/10 text-gray-600 border-gray-500/20',
};

const statusLabels = {
  lead: 'Lead',
  customer: 'Cliente',
  inactive: 'Inattivo',
};

const CustomersPage = () => {
  const { API, getAuthHeaders } = useAuth();
  const [searchParams] = useSearchParams();
  const [customers, setCustomers] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    customer_status: 'lead',
    tags: [],
    notes: '',
    birth_date: '',
    preferred_services: []
  });

  const fetchData = async () => {
    try {
      const [custRes, svcRes] = await Promise.all([
        axios.get(`${API}/customers`, getAuthHeaders()),
        axios.get(`${API}/services`, getAuthHeaders()),
      ]);
      setCustomers(custRes.data);
      setServices(svcRes.data);
    } catch (error) {
      console.error('Errore nel caricamento:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedCustomer) {
        await axios.put(`${API}/customers/${selectedCustomer.id}`, formData, getAuthHeaders());
      } else {
        await axios.post(`${API}/customers`, formData, getAuthHeaders());
      }
      fetchData();
      resetForm();
    } catch (error) {
      console.error('Errore nel salvataggio cliente:', error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo cliente?')) return;
    try {
      await axios.delete(`${API}/customers/${id}`, getAuthHeaders());
      fetchData();
    } catch (error) {
      console.error('Errore nell\'eliminazione cliente:', error);
    }
  };

  const resetForm = () => {
    setFormData({ first_name: '', last_name: '', email: '', phone: '', customer_status: 'lead', tags: [], notes: '', birth_date: '', preferred_services: [] });
    setSelectedCustomer(null);
    setIsDialogOpen(false);
  };

  const openEdit = (customer) => {
    setSelectedCustomer(customer);
    setFormData({
      first_name: customer.first_name || (customer.name || '').split(' ')[0] || '',
      last_name: customer.last_name || (customer.name || '').split(' ').slice(1).join(' ') || '',
      email: customer.email || '',
      phone: customer.phone || '',
      customer_status: customer.customer_status,
      tags: customer.tags || [],
      notes: customer.notes || '',
      birth_date: customer.birth_date || '',
      preferred_services: customer.preferred_services || []
    });
    setIsDialogOpen(true);
  };

  const toggleService = (serviceId) => {
    setFormData(prev => ({
      ...prev,
      preferred_services: prev.preferred_services.includes(serviceId)
        ? prev.preferred_services.filter(id => id !== serviceId)
        : [...prev.preferred_services, serviceId]
    }));
  };

  const getServiceName = (serviceId) => {
    const s = services.find(sv => sv.id === serviceId);
    return s ? s.name : serviceId;
  };

  const filteredCustomers = customers.filter(c => {
    const fullName = c.name || `${c.first_name || ''} ${c.last_name || ''}`;
    const matchesSearch = fullName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.phone?.includes(searchQuery);
    const matchesStatus = statusFilter === 'all' || c.customer_status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="customers-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-outfit">Clienti</h1>
          <p className="text-muted-foreground mt-1">Gestisci le relazioni con i tuoi clienti</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { if (!open) resetForm(); else setIsDialogOpen(true); }}>
          <DialogTrigger asChild>
            <Button data-testid="add-customer-btn">
              <Plus className="w-4 h-4 mr-2" />
              Aggiungi Cliente
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-outfit">
                {selectedCustomer ? 'Modifica Cliente' : 'Aggiungi Nuovo Cliente'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="first_name">Nome *</Label>
                  <Input
                    id="first_name"
                    value={formData.first_name}
                    onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                    placeholder="Mario"
                    required
                    data-testid="customer-firstname-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name">Cognome</Label>
                  <Input
                    id="last_name"
                    value={formData.last_name}
                    onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                    placeholder="Rossi"
                    data-testid="customer-lastname-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="mario@esempio.com"
                    data-testid="customer-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Telefono</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    placeholder="+39 333 1234567"
                    data-testid="customer-phone-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="birth_date">Data di Nascita</Label>
                  <Input
                    id="birth_date"
                    type="date"
                    value={formData.birth_date || ''}
                    onChange={(e) => setFormData({...formData, birth_date: e.target.value})}
                    data-testid="customer-birthdate-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="status">Stato</Label>
                  <Select value={formData.customer_status} onValueChange={(v) => setFormData({...formData, customer_status: v})}>
                    <SelectTrigger data-testid="customer-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="lead">Lead</SelectItem>
                      <SelectItem value="customer">Cliente</SelectItem>
                      <SelectItem value="inactive">Inattivo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <label className="flex items-start gap-3 p-3 rounded-lg border bg-muted/20 cursor-pointer"
                data-testid="customer-vip-toggle">
                <Checkbox
                  checked={(formData.tags || []).some((t) => t.toLowerCase() === 'vip')}
                  onCheckedChange={(v) => setFormData({
                    ...formData,
                    tags: v
                      ? [...(formData.tags || []).filter((t) => t.toLowerCase() !== 'vip'), 'vip']
                      : (formData.tags || []).filter((t) => t.toLowerCase() !== 'vip'),
                  })}
                />
                <span className="min-w-0">
                  <span className="text-sm font-medium flex items-center gap-1.5">
                    <Crown className="w-4 h-4 text-amber-500" />Cliente VIP
                  </span>
                  <span className="block text-xs text-muted-foreground mt-0.5">
                    Entra nel gruppo "Clienti VIP" delle promozioni. Ci finisce comunque chi ha almeno 5 appuntamenti.
                  </span>
                </span>
              </label>

              {/* Preferred Services */}              {services.length > 0 && (
                <div className="space-y-2">
                  <Label className="flex items-center gap-1.5"><Scissors className="w-4 h-4" />Servizi Abituali</Label>
                  <div className="grid grid-cols-2 gap-2 p-3 rounded-lg border bg-muted/20 max-h-[160px] overflow-y-auto">
                    {services.filter(s => s.is_active !== false).map((service) => (
                      <label
                        key={service.id}
                        className="flex items-center gap-2 cursor-pointer p-1.5 rounded hover:bg-muted/50 transition-colors"
                        data-testid={`service-check-${service.id}`}
                      >
                        <Checkbox
                          checked={formData.preferred_services.includes(service.id)}
                          onCheckedChange={() => toggleService(service.id)}
                        />
                        <span className="text-sm truncate">{service.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="notes">Note</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  placeholder="Aggiungi note su questo cliente..."
                  rows={2}
                  data-testid="customer-notes-input"
                />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <Button type="button" variant="outline" onClick={resetForm}>Annulla</Button>
                <Button type="submit" data-testid="save-customer-btn">
                  {selectedCustomer ? 'Aggiorna' : 'Aggiungi'} Cliente
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filtri */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Cerca clienti..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="search-customers"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-40" data-testid="status-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Stato" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti gli Stati</SelectItem>
                <SelectItem value="lead">Lead</SelectItem>
                <SelectItem value="customer">Clienti</SelectItem>
                <SelectItem value="inactive">Inattivi</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Lista Clienti */}
      {filteredCustomers.length > 0 ? (
        <div className="grid gap-4">
          {filteredCustomers.map((customer, index) => (
            <Card
              key={customer.id}
              className="card-hover animate-fade-in"
              style={{ animationDelay: `${index * 0.05}s` }}
              data-testid={`customer-card-${customer.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-4 flex-1 min-w-0">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <User className="w-6 h-6 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 flex-wrap">
                        <h3 className="font-semibold font-outfit truncate">{customer.name}</h3>
                        <Badge className={statusColors[customer.customer_status]} variant="outline">
                          {statusLabels[customer.customer_status]}
                        </Badge>
                        {(customer.tags || []).some((t) => t.toLowerCase() === 'vip') && (
                          <Badge variant="outline" className="gap-1 text-amber-600 border-amber-500/40"
                            data-testid={`vip-badge-${customer.id}`}>
                            <Crown className="w-3 h-3" />VIP
                          </Badge>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-sm text-muted-foreground">
                        {customer.email && (
                          <span className="flex items-center gap-1">
                            <Mail className="w-3.5 h-3.5" />
                            {customer.email}
                          </span>
                        )}
                        {customer.phone && (
                          <span className="flex items-center gap-1">
                            <Phone className="w-3.5 h-3.5" />
                            {customer.phone}
                          </span>
                        )}
                        {customer.birth_date && (
                          <span className="flex items-center gap-1">
                            <Cake className="w-3.5 h-3.5" />
                            {new Date(customer.birth_date).toLocaleDateString('it-IT')}
                          </span>
                        )}
                      </div>
                      {/* Preferred services badges */}
                      {customer.preferred_services?.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          {customer.preferred_services.map(sId => (
                            <Badge key={sId} variant="secondary" className="text-[10px] gap-1">
                              <Scissors className="w-2.5 h-2.5" />{getServiceName(sId)}
                            </Badge>
                          ))}
                        </div>
                      )}
                      {customer.notes && (
                        <p className="text-sm text-muted-foreground mt-2 line-clamp-1">{customer.notes}</p>
                      )}
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" data-testid={`customer-menu-${customer.id}`}>
                        <MoreHorizontal className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => openEdit(customer)}>
                        <Edit className="w-4 h-4 mr-2" />
                        Modifica
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => handleDelete(customer.id)}
                        className="text-destructive focus:text-destructive"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Elimina
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="p-12 text-center">
            <User className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
            <h3 className="font-semibold font-outfit mb-1">Nessun cliente trovato</h3>
            <p className="text-sm text-muted-foreground mb-4">
              {searchQuery || statusFilter !== 'all'
                ? 'Prova a modificare i filtri'
                : 'Aggiungi il tuo primo cliente per iniziare'}
            </p>
            {!searchQuery && statusFilter === 'all' && (
              <Button onClick={() => setIsDialogOpen(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Aggiungi Cliente
              </Button>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CustomersPage;
